import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import api from "@/lib/api";
import ThemeSwitcher from "@/components/ThemeSwitcher";
import BrandMark from "@/components/BrandMark";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Bell, MessageCircle, LayoutDashboard, LogOut, User, Store, Shield, LayoutGrid, ChevronDown } from "lucide-react";

const APPS = [
  { id: "gharonda", to: "/apps/gharonda", color: "#0ea5e9", iconBg: "linear-gradient(135deg,#0284c7,#0ea5e9)" },
  { id: "khavayye", to: "/apps/khavayye", color: "#dc2626", iconBg: "linear-gradient(135deg,#b91c1c,#f97316)" },
  { id: "soulmates", to: "/apps/soulmates", color: "#a855f7", iconBg: "linear-gradient(135deg,#9333ea,#ec4899)" },
  { id: "sabkuchfresh", to: "/apps/sabkuchfresh", color: "#16a34a", iconBg: "linear-gradient(135deg,#15803d,#65a30d)" },
  { id: "niramaya", to: "/apps/niramaya", color: "#0d9488", iconBg: "linear-gradient(135deg,#134e4a,#14b8a6)" },
  { id: "rozgari", to: "/apps/rozgari", color: "#6d28d9", iconBg: "linear-gradient(135deg,#4c1d95,#f97316)" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const { t } = useLang();
  const [unread, setUnread] = useState(0);
  const [soulmatesAllowed, setSoulmatesAllowed] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/matrimonial/access")
      .then((r) => setSoulmatesAllowed(Boolean(r.data?.allowed)))
      .catch(() => setSoulmatesAllowed(false));
  }, [user]);

  useEffect(() => {
    if (!user) return;
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/chats/unread-count");
        if (!stop) setUnread(data.count || 0);
      } catch (_) { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 10000);
    return () => { stop = true; clearInterval(id); };
  }, [user]);

  const initials = (user?.name || "U")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border/60 bg-background/80 backdrop-blur-xl">
      <div className="relative flex h-[184px] w-full items-center justify-between pl-2 pr-4 sm:pr-6 lg:pr-8">
        <Link to="/" className="flex items-center gap-3 shrink-0 relative z-10" data-testid="header-logo">
          <BrandMark width={175} height={170} />
        </Link>

        {isHome && !user && (
          <div
            className="hidden md:flex pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 flex-col items-center justify-center leading-tight px-4"
            data-testid="header-saffron-tagline"
          >
            <span
              className="font-heading text-2xl lg:text-3xl xl:text-4xl tracking-tight text-center whitespace-nowrap"
              style={{
                color: "#ff9933",
                textShadow: "0 1px 0 rgba(255,153,51,0.15), 0 2px 12px rgba(255,153,51,0.32)",
              }}
            >
              ॥ एक मेकां साहाय्य करू&nbsp; अवघे धरू सुपंथ ॥
            </span>
            <span className="text-[10px] lg:text-[11px] uppercase tracking-[0.26em] text-[#c26f1d]/85 mt-1.5 text-center">
              Let us help one another — together we walk the noble path
            </span>
          </div>
        )}

        <nav className="hidden md:flex items-center gap-1">
          {user && (
            <>
              <NavLink
                to="/cities"
                data-testid="nav-cities"
                className={({ isActive }) =>
                  `px-3 py-1.5 text-sm rounded-full transition-colors ${
                    isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                {t("nav.cities")}
              </NavLink>
              <NavLink
                to="/categories"
                data-testid="nav-categories"
                className={({ isActive }) =>
                  `px-3 py-1.5 text-sm rounded-full transition-colors ${
                    isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                {t("nav.categories")}
              </NavLink>
              <NavLink
                to="/vendors"
                data-testid="nav-vendors"
                className={({ isActive }) =>
                  `px-3 py-1.5 text-sm rounded-full transition-colors ${
                    isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                {t("nav.vendors")}
              </NavLink>
              <NavLink
                to="/items"
                data-testid="nav-items"
                className={({ isActive }) =>
                  `px-3 py-1.5 text-sm rounded-full transition-colors ${
                    isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                Items
              </NavLink>
              <NavLink
                to="/inquiries"
                data-testid="nav-inquiries"
                className={({ isActive }) =>
                  `px-3 py-1.5 text-sm rounded-full transition-colors ${
                    isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                Inquiries
              </NavLink>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="nav-apps-trigger"
                    className="px-3 py-1.5 text-sm rounded-full inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <LayoutGrid className="h-3.5 w-3.5" />
                    {t("nav.apps")}
                    <ChevronDown className="h-3 w-3 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-[320px] p-2" data-testid="nav-apps-menu">
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground px-2 pb-1.5">
                    {t("apps.menu_subtitle")}
                  </DropdownMenuLabel>
                  {APPS.filter((a) => a.id !== "soulmates" || soulmatesAllowed).map((a) => (
                    <DropdownMenuItem
                      asChild
                      key={a.id}
                      data-testid={`nav-apps-${a.id}`}
                      className="rounded-xl cursor-pointer p-2 gap-3 focus:bg-muted"
                    >
                      <Link to={a.to}>
                        <span
                          className="h-9 w-9 rounded-xl text-white flex items-center justify-center shrink-0 font-heading"
                          style={{ background: a.iconBg }}
                        >
                          {t(`apps.${a.id}.name`).slice(0, 1)}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm leading-tight truncate">{t(`apps.${a.id}.name`)}</div>
                          <div className="text-[11px] text-muted-foreground truncate">{t(`apps.${a.id}.tagline`)}</div>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </nav>

        <div className="flex items-center gap-2">
          <ThemeSwitcher />
          {!user ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/login")}
                data-testid="header-login-btn"
              >
                {t("header.login")}
              </Button>
              <Button
                size="sm"
                onClick={() => navigate("/register")}
                data-testid="header-invite-btn"
                className="rounded-full"
              >
                {t("header.invite")}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/notifications")}
                data-testid="header-notifications-btn"
                aria-label={t("header.notifications")}
              >
                <Bell className="h-5 w-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/chat")}
                data-testid="header-chat-btn"
                aria-label={t("header.messages")}
                className="relative"
              >
                <MessageCircle className="h-5 w-5" />
                {unread > 0 && (
                  <span
                    data-testid="header-chat-unread-badge"
                    className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold flex items-center justify-center ring-2 ring-background"
                  >
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-muted transition-colors"
                    data-testid="header-user-menu"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user.picture} />
                      <AvatarFallback>{initials}</AvatarFallback>
                    </Avatar>
                    <span className="hidden lg:inline text-sm font-medium">{user.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="flex flex-col">
                    <span>{user.name}</span>
                    <span className="text-xs text-muted-foreground font-normal">{user.email}</span>
                    <Badge variant="secondary" className="mt-1 self-start text-[10px] uppercase">
                      {user.role}
                    </Badge>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                    <User className="h-4 w-4 mr-2" /> {t("menu.profile")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/apps/soulmates")} data-testid="menu-apply-matrimonial">
                    <span className="mr-2 text-pink-600 text-base leading-none">♡</span> Apply for Matrimonial
                  </DropdownMenuItem>
                  {user.role === "vendor" && (
                    <DropdownMenuItem onClick={() => navigate("/dashboard/vendor")} data-testid="menu-vendor-dashboard">
                      <LayoutDashboard className="h-4 w-4 mr-2" /> {t("menu.vendor_dashboard")}
                    </DropdownMenuItem>
                  )}
                  {user.role === "consumer" && (
                    <DropdownMenuItem onClick={() => navigate("/become-vendor")} data-testid="menu-become-vendor">
                      <Store className="h-4 w-4 mr-2" /> {t("menu.become_vendor")}
                    </DropdownMenuItem>
                  )}
                  {(user.role === "admin" || user.role === "superadmin") && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                      <Shield className="h-4 w-4 mr-2" /> {t("menu.admin")}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={async () => {
                      await logout();
                      navigate("/");
                    }}
                    data-testid="menu-logout"
                  >
                    <LogOut className="h-4 w-4 mr-2" /> {t("menu.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
